'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetImageModerationStatus } from '@morfi/backend-sdk-v2/client';
import { useEffect, useState } from 'react';
import { useUploadTrackingStore } from '@/stores/upload-tracking';
const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 1500;
export interface NsfwStatusByUrl {
  /** True once the moderator has classified this URL as flagged. */
  isFlagged: boolean;
  /** True only for a confirmed flagged or minors-takedown verdict. */
  isNsfw: boolean;
  /** True while still awaiting a verdict (or sha256 unknown). */
  isPending: boolean;
  /** Raw status, when known. */
  status: 'pending' | 'clean' | 'flagged' | 'minors_takedown' | 'failed' | undefined;
  /** True when this exact URL belongs to the current upload session. */
  isTracked: boolean;
}

/**
 * Resolve NSFW moderation status for a freshly-uploaded image URL using
 * the URL→sha256 map stashed by `<CropModal>` on upload. Polls
 * `/v1/images/{sha256}/moderation-status` every 1.5s until a verdict
 * lands or 12s elapses. After that the cron sweeper takes over server-
 * side and the viewer-side JOIN surfaces the verdict on next render.
 *
 * Returns `isPending=true` when:
 * - The URL isn't in the upload-tracking store (e.g. preset image, page
 *   refresh after upload), in which case there's nothing to poll.
 * - A verdict has not yet landed.
 */
export function useNsfwStatusByUrl(url) {
  const $ = _c(32);
  let t0;
  if ($[0] !== url) {
    t0 = s => url === null || url === undefined || url === "" ? undefined : s.urlToSha.get(url);
    $[0] = url;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const sha256 = useUploadTrackingStore(t0);
  let t1;
  if ($[2] !== url) {
    t1 = s_0 => url === null || url === undefined || url === "" ? undefined : s_0.urlToStatus.get(url);
    $[2] = url;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const knownStatus = useUploadTrackingStore(t1);
  const [polling, setPolling] = useState(null);
  const attempts = polling !== null && polling.sha256 === sha256 ? polling.attempts : 0;
  const trackUpload = useUploadTrackingStore(_temp);
  const t2 = sha256 ?? "";
  let t3;
  if ($[4] !== t2) {
    t3 = {
      sha256: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = knownStatus === undefined && sha256 !== undefined && attempts < MAX_POLL_ATTEMPTS;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      enabled: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const moderation = useGetImageModerationStatus(t3, t5);
  const status = knownStatus ?? moderation.data?.status;
  const error = moderation.error;
  const giveUp = error !== undefined;
  const refetch = moderation.refetch;
  let t6;
  let t7;
  if ($[8] !== attempts || $[9] !== giveUp || $[10] !== knownStatus || $[11] !== refetch || $[12] !== sha256 || $[13] !== status) {
    t6 = () => {
      if (knownStatus !== undefined) {
        return;
      }
      if (sha256 === undefined) {
        return;
      }
      if (status !== undefined && status !== "pending") {
        return;
      }
      if (attempts >= MAX_POLL_ATTEMPTS) {
        return;
      }
      if (giveUp) {
        return;
      }
      const t = setTimeout(() => {
        refetch();
        setPolling({
          sha256,
          attempts: attempts + 1
        });
      }, POLL_INTERVAL_MS);
      return () => {
        clearTimeout(t);
      };
    };
    t7 = [knownStatus, sha256, status, attempts, refetch, giveUp];
    $[8] = attempts;
    $[9] = giveUp;
    $[10] = knownStatus;
    $[11] = refetch;
    $[12] = sha256;
    $[13] = status;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] !== moderation.data?.status || $[17] !== sha256 || $[18] !== trackUpload || $[19] !== url) {
    t8 = () => {
      const resolved = moderation.data?.status;
      if (url === null || url === undefined || url.length === 0 || sha256 === undefined || resolved === undefined || resolved === "pending") {
        return;
      }
      trackUpload(url, sha256, resolved);
    };
    $[16] = moderation.data?.status;
    $[17] = sha256;
    $[18] = trackUpload;
    $[19] = url;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = moderation.data?.status;
  let t10;
  if ($[21] !== sha256 || $[22] !== t9 || $[23] !== trackUpload || $[24] !== url) {
    t10 = [t9, sha256, trackUpload, url];
    $[21] = sha256;
    $[22] = t9;
    $[23] = trackUpload;
    $[24] = url;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  useEffect(t8, t10);
  const hasUrl = url !== null && url !== undefined && url !== "";
  const isFlagged = status === "flagged" || status === "minors_takedown";
  const isPending = hasUrl && knownStatus === undefined && !giveUp && (sha256 === undefined || status === undefined || status === "pending" && attempts < MAX_POLL_ATTEMPTS);
  const isNsfw = isFlagged;
  const t11 = sha256 !== undefined;
  let t12;
  if ($[26] !== isFlagged || $[27] !== isNsfw || $[28] !== isPending || $[29] !== status || $[30] !== t11) {
    t12 = {
      isFlagged,
      isNsfw,
      isPending,
      status,
      isTracked: t11
    };
    $[26] = isFlagged;
    $[27] = isNsfw;
    $[28] = isPending;
    $[29] = status;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
}
function _temp(s_1) {
  return s_1.track;
}